import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber } from '../../../utils/formatUtils';
import { FormField } from './shared';

const IRPFCalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [salario, setSalario] = useState<string>('5.000,00');
  const [dependentes, setDependentes] = useState<string>('0');
  const [outrosDescontos, setOutrosDescontos] = useState<string>('0,00');
  const [result, setResult] = useState<any>(null);

  const calculate = () => {
    const bruto = parseFloat(parseBrazilianNumber(salario)) || 0;
    const descExtra = parseFloat(parseBrazilianNumber(outrosDescontos)) || 0;
    const deps = parseInt(parseBrazilianNumber(dependentes)) || 0;
    if (bruto <= 0) return null;

    // 1. Cálculo INSS 2024 (Tabela Progressiva)
    let inss = 0;
    if (bruto <= 1412.00) inss = bruto * 0.075;
    else if (bruto <= 2666.68) inss = (1412 * 0.075) + ((bruto - 1412) * 0.09);
    else if (bruto <= 4000.03) inss = (1412 * 0.075) + ((2666.68 - 1412) * 0.09) + ((bruto - 2666.68) * 0.12);
    else if (bruto <= 7786.02) inss = (1412 * 0.075) + ((2666.68 - 1412) * 0.09) + ((4000.03 - 2666.68) * 0.12) + ((bruto - 4000.03) * 0.14);
    else inss = 908.85; // Teto INSS

    // 2. Base de Cálculo IRPF
    const baseComDeducoes = bruto - inss - (deps * 189.59) - descExtra;
    const baseSimplificada = bruto - inss - 528.00; // Desconto simplificado padrão

    // O governo aplica o que for mais vantajoso para o contribuinte automaticamente
    const baseEfetiva = Math.max(0, Math.min(baseComDeducoes, baseSimplificada));
    const isSimplificado = baseSimplificada < baseComDeducoes;

    // 3. Cálculo IRPF (Tabela 2024)
    let irpf = 0;
    let aliquota = 0;
    if (baseEfetiva <= 2112.00) { irpf = 0; aliquota = 0; }
    else if (baseEfetiva <= 2826.65) { irpf = (baseEfetiva * 0.075) - 158.40; aliquota = 7.5; }
    else if (baseEfetiva <= 3751.05) { irpf = (baseEfetiva * 0.15) - 370.40; aliquota = 15; }
    else if (baseEfetiva <= 4664.68) { irpf = (baseEfetiva * 0.225) - 651.73; aliquota = 22.5; }
    else { irpf = (baseEfetiva * 0.275) - 884.96; aliquota = 27.5; }

    const res = {
      bruto,
      inss,
      irpf: Math.max(0, irpf),
      liquido: bruto - inss - Math.max(0, irpf),
      aliquota,
      metodo: isSimplificado ? 'Desconto Simplificado' : 'Deduções na Base'
    };

    setResult(res);

    onResultChange([
      { label: 'Salário Bruto', value: `R$ ${res.bruto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'INSS', value: `R$ ${res.inss.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'IRPF', value: `R$ ${res.irpf.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Alíquota IRPF', value: `${res.aliquota}%` },
      { label: 'Salário Líquido', value: `R$ ${res.liquido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Método', value: res.metodo }
    ]);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-emerald-50 to-slate-50 rounded-full border border-emerald-200 mb-4">
          <span className="text-2xl">🧾</span>
          <span className="text-sm font-black text-theme-text uppercase tracking-wider">IRPF</span>
        </div>
        <p className="text-theme-muted text-sm">Estimativa mensal com INSS e tabela progressiva</p>
      </div>

      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField
            label="Salário Bruto (R$)"
            placeholder="Ex: 5.000,00"
            value={salario}
            onChange={setSalario}
            helpText="Salário mensal bruto"
            required
            isNumeric
          />

          <FormField
            label="Dependentes"
            placeholder="Ex: 0"
            value={dependentes}
            onChange={setDependentes}
            helpText="Quantidade de dependentes"
            required
            isInteger
          />

          <FormField
            label="Deduções (R$)"
            placeholder="Ex: 0,00"
            value={outrosDescontos}
            onChange={setOutrosDescontos}
            helpText="Outros descontos legais"
            isNumeric
          />
        </div>
      </div>

      <div className="flex justify-center">
        <button
          onClick={calculate}
          className="px-12 py-5 bg-accent-gradient text-white rounded-[2rem] font-black shadow-accent hover:shadow-accent-hover hover:scale-[1.02] transition-all hover:-translate-y-1 active:scale-95 uppercase text-sm tracking-widest flex items-center gap-3"
        >
          <Icon name="play" className="w-5 h-5" />
          Calcular IRPF
        </button>
      </div>

      {result ? (
        <div className="relative">
          <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 opacity-10 blur-3xl" />
          <div className="relative p-10 rounded-[2.5rem] text-center shadow-2xl border-2 bg-gradient-to-br from-emerald-500 to-teal-500 border-teal-400 text-white">
            <div className="flex justify-center mb-5">
              <div className="w-20 h-20 rounded-full bg-theme-result/20 backdrop-blur-sm flex items-center justify-center text-4xl shadow-xl">💰</div>
            </div>

            <div className="mb-5">
              <p className="text-xs font-black uppercase tracking-widest opacity-70 mb-2">Salário Líquido</p>
              <p className="text-3xl font-black mb-2">R$ {result.liquido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              <div className="w-20 h-1 bg-theme-result/30 rounded-full mx-auto" />
            </div>

            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">INSS</p>
                <p className="text-2xl font-black">R$ {result.inss.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs opacity-70 mt-1">previdência</p>
              </div>
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">IRPF</p>
                <p className="text-2xl font-black">R$ {result.irpf.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs opacity-70 mt-1">alíquota: {result.aliquota}%</p>
              </div>
            </div>

            <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4 border border-white/20">
              <p className="text-xs font-black uppercase opacity-70 mb-2">📌 Método</p>
              <p className="text-sm font-medium">{result.metodo}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="h-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 rounded-[2.5rem] border-2 border-dashed border-theme-border text-theme-muted p-10 text-center">
          <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center text-2xl mb-3">🧾</div>
          <p className="font-bold text-base">Insira o salário bruto para ver o cálculo</p>
        </div>
      )}

      {/* Dicas Adicionais */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-theme-input p-4 rounded-2xl border border-theme-border">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">💡</span>
            <span className="text-sm font-black text-theme-text">Deduções</span>
          </div>
          <p className="text-xs text-theme-muted">Dependentes: R$ 189,59 cada um. Plano saúde, educação, etc.</p>
        </div>
        <div className="bg-theme-input p-4 rounded-2xl border border-theme-border">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">📊</span>
            <span className="text-sm font-black text-theme-text">Tabela</span>
          </div>
          <p className="text-xs text-theme-muted">Alíquotas progressivas de 7,5% a 27,5% conforme renda</p>
        </div>
        <div className="bg-theme-input p-4 rounded-2xl border border-theme-border">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🎯</span>
            <span className="text-sm font-black text-theme-text">Otimização</span>
          </div>
          <p className="text-xs text-theme-muted">Sistema escolhe automaticamente o melhor método de cálculo</p>
        </div>
      </div>
    </div>
  );
};

export default IRPFCalculator;
